/**
 * VideoPreviewStep
 *
 * Second step of the add content flow.
 * Shows video thumbnail with duration, premium, and 4K labels.
 * Matches Flutter AssetThumbnail overlay pattern from pick_video_screen.dart.
 */

import React, { useCallback, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { MediaType } from '@/domain/enums/mediaType';
import { useRegisterStepNav } from '@/presentation/components/common/stepper';
import type { VideoMeta } from '../../hooks/useAddContent';
import { VideoPreviewPlayer, type VideoPreviewPlayerRef } from './VideoPreviewPlayer';

interface VideoPreviewStepProps {
  file: File | null;
  videoMeta: VideoMeta | null;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Message-media flow (chat OR community): the primary action becomes "Done"
   * (submits the upload via `onMessageSubmit` instead of advancing the stepper)
   * and the premium / 4K / premium-message overlays are suppressed (message
   * content has no monetization, Flutter `preview_content_screen.dart:119-172`).
   */
  isMessageFlow?: boolean;
  onMessageSubmit?: () => void | Promise<void>;
  /** Resolved media type for the message flow (images skip the duration label). */
  mediaType?: MediaType | null;
}

export const VideoPreviewStep: React.FC<VideoPreviewStepProps> = ({
  file,
  videoMeta,
  onConfirm,
  onCancel,
  isMessageFlow = false,
  onMessageSubmit,
  mediaType,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const playerRef = useRef<VideoPreviewPlayerRef>(null);

  const handleConfirm = useCallback(async () => {
    playerRef.current?.pause();
    if (isMessageFlow) {
      await onMessageSubmit?.();
      return;
    }
    onConfirm();
  }, [isMessageFlow, onMessageSubmit, onConfirm]);

  const handleCancel = useCallback(() => {
    playerRef.current?.pause();
    onCancel();
  }, [onCancel]);

  useRegisterStepNav(
    {
      onNext: handleConfirm,
      onPrevious: handleCancel,
      // Message flow: the stepper's default "Next" becomes "Done".
      nextLabel: isMessageFlow ? l10n.publish.chatDoneButton : undefined,
    },
    [handleConfirm, handleCancel, isMessageFlow, l10n],
  );

  if (!videoMeta || !file) return null;

  // Premium / 4K monetization overlays only apply to user content; the message
  // flow (chat / community) has none (Flutter preview_content_screen.dart:119-172).
  const isPremium = !isMessageFlow && MediaUtils.isPremiumContent(videoMeta.duration);
  const is4k = !isMessageFlow && MediaUtils.is4kVideo(Math.min(videoMeta.width, videoMeta.height));
  const premiumCoins = MediaUtils.getPremiumContentCoins(videoMeta.duration);
  // Images (message flow only) have no duration; skip the label for them. The
  // user flow is video-only and never passes `mediaType`, so this stays true there.
  const showDurationLabel = !(isMessageFlow && mediaType === MediaType.IMAGE);
  const durationLabel = MediaUtils.formatDurationFromSeconds(videoMeta.duration);

  const labelStyle: React.CSSProperties = {
    backgroundColor: theme.modalOverlay,
    borderRadius: responsive.borderRadius.sm,
    padding: responsive.spacing.xs,
    display: 'flex',
    alignItems: 'center',
    gap: responsive.spacing.xs,
  };

  const labelTextStyle: React.CSSProperties = {
    color: theme.white,
    fontSize: responsive.font.xs,
  };

  const freeMinutes = MathUtils.secondsToMinutesCeil(remoteConfigService.freeContentMaxSeconds);
  const coinsPerMin = remoteConfigService.premiumContentCoinsPerMinute;

  const premiumMessage = isPremium
    ? l10n.publish.premiumVideoMessage
      .replace('{freeMin}', String(freeMinutes))
      .replace('{coinsPerMin}', String(coinsPerMin))
      .replace('{coins}', String(premiumCoins))
    : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        padding: responsive.spacing.xl,
        gap: responsive.spacing.lg,
      }}
    >
      {/* Thumbnail with overlays */}
      <div
        style={{
          position: 'relative',
          // allow max width
          // maxWidth: responsive.videoPreview.thumbnailSize,
          maxHeight: '70%',
          aspectRatio: `${videoMeta.width} / ${videoMeta.height}`,
          borderRadius: responsive.borderRadius.base,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <VideoPreviewPlayer
          ref={playerRef}
          file={file}
          thumbnailUrl={videoMeta.thumbnailUrl}
          duration={videoMeta.duration}
          mediaType={mediaType}
        />

        {/* Duration label - bottom right (skipped for images) */}
        {showDurationLabel && (
          <div
            style={{
              position: 'absolute',
              bottom: responsive.spacing.xs,
              right: responsive.spacing.xs,
              ...labelStyle,
            }}
          >
            <span style={labelTextStyle}>{durationLabel}</span>
          </div>
        )}

        {/* Premium label - top right */}
        {isPremium && (
          <div
            style={{
              position: 'absolute',
              top: responsive.spacing.xs,
              right: responsive.spacing.xs,
              ...labelStyle,
            }}
          >
            <AssetIcon path="/assets/images/premium_content.png" width={responsive.spacing.base} height={responsive.spacing.base} />
            <span style={labelTextStyle}>
              {premiumCoins} {l10n.common.coins}
            </span>
          </div>
        )}

        {/* 4K label - top left */}
        {is4k && (
          <div
            style={{
              position: 'absolute',
              top: responsive.spacing.xs,
              left: responsive.spacing.xs,
              ...labelStyle,
            }}
          >
            <span style={labelTextStyle}>{l10n.content.fourK}</span>
          </div>
        )}
      </div>

      {/* Premium message */}
      {premiumMessage && (
        <p
          style={{
            color: theme.text.secondary,
            fontSize: responsive.font.sm,
            textAlign: 'center',
            whiteSpace: 'pre-line',
          }}
        >
          {premiumMessage}
        </p>
      )}

    </div>
  );
};
